/**
 * Turning lesson blocks into words.
 *
 * The reader highlights at word granularity, so every piece of body text has to
 * become a list of word objects carrying a page-wide `globalIndex` — that index
 * is what a saved highlight stores, and what every later lookup joins on.
 *
 * All of this is a pure function of (content, highlightMap). It lives outside
 * the component so it can be read and tested without mounting a page, and so
 * the renderer is left holding only the parts that genuinely need a DOM.
 */

import { formatVerseReference } from '@/utils/bible-verse';

// Blocks whose body is a rich-text run — the same `inline` shape a paragraph has,
// minus the interactive items. All of them are ordinary prose a reader may want to
// mark, so all of them are tokenised the same way.
const RICH_TEXT_BLOCK_TYPES = new Set(['quote', 'accordion', 'leader-note']);

const VERSE_BLOCK_TYPES = new Set(['verse', 'scripture-memory-verse']);

// What a `blank` block's prompt uses to mark where an input goes, and how one
// answer is separated from the next in the stored answer string.
const BLANK_MARKER = '____';
const ANSWER_SEPARATOR = '|';

// What an unfilled blank contributes to a highlight's quote. A reader who marks a
// sentence before answering it gets the shape of the sentence, with the gap still
// showing as a gap.
export const BLANK_PLACEHOLDER = '___';

/**
 * The answers currently shown in a block's blanks, one per blank, in order.
 *
 * A block stores its answers as a single separator-joined string, and the leader
 * view shows the answer key instead of the reader's own work — the same rule
 * BlankBlock renders by, kept here so the words and the inputs cannot disagree.
 */
export function blankAnswersFor(block, { answers = {}, showAnswers = false } = {}) {
  const source = showAnswers
    ? block.correctAnswer || ''
    : answers[block.id] || answers[block._uid] || '';
  return String(source)
    .split(ANSWER_SEPARATOR)
    .map(answer => answer.trim());
}

// A blank is ALWAYS one word, whatever is typed into it — a two-word answer does
// not become two words. Anything else would renumber the page as the reader types
// and move every highlight after the blank while they were mid-answer.
//
// The PLAIN reading, because this is what the blank contributes to the page's word
// stream: a highlight quoting the sentence should read as the sentence, not carry
// the answer key's asterisks into a saved quote.
export function blankWordText(answer) {
  const text = stripInlineMarkup(String(answer ?? ''));
  return text || BLANK_PLACEHOLDER;
}

/**
 * Strip lightweight Markdown conventions from a string.
 *
 * Two callers, for the same reason: a literal marker is never what the author
 * meant. The normaliser runs verse text and a blank's answer key through this, and
 * the answer key is the one that matters most — the matcher reduces punctuation to
 * spaces, so `**faith**` survives by luck while `fa**i**th` becomes "fa i th" and
 * a member typing the right word is marked wrong.
 *
 * Distinct from parseEmphasis below, which REMOVES `**bold**` markers and reports
 * where the emphasis was so the words can be styled. This one only removes.
 */
export function stripInlineMarkup(text) {
  if (typeof text !== 'string') return '';
  return text
    .replace(/\*\*([\s\S]+?)\*\*/g, '$1')
    .replace(/__([\s\S]+?)__/g, '$1')
    .replace(/\*([^*\n]+?)\*/g, '$1')
    .replace(/_([^_\n]+?)_/g, '$1')
    .replace(/~~([\s\S]+?)~~/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
}

// A `**bold**` span, captured so it can be held back from the stripper below.
const BOLD_SPAN = /(\*\*[\s\S]+?\*\*)/g;

/**
 * Strip inline markdown, but leave `**bold**` markers in place.
 *
 * For a rich body whose flat mirror is built with stripInlineMarkup: the two have
 * to read the same, or a verse that gains a rich body would suddenly start
 * printing the `*italic*` and `~~strike~~` markers its plain mirror has always
 * hidden. `**` is the exception because processText still parses it into a real
 * bold mark further down, which is how authored verse emphasis works today.
 *
 * Stripping cannot simply skip the `**` rule: `*([^*\n]+?)*` matches INSIDE
 * `**bold**` and would chew it down to `*bold*`. The bold spans are split out
 * first and their contents stripped separately.
 */
export function stripInlineMarkupKeepingBold(text) {
  if (typeof text !== 'string') return '';
  return text
    .split(BOLD_SPAN)
    .map(part =>
      part.startsWith('**') && part.endsWith('**') && part.length > 4
        ? `**${stripInlineMarkup(part.slice(2, -2))}**`
        : stripInlineMarkup(part),
    )
    .join('');
}

/**
 * The markup a blank's ANSWER KEY may carry, and the mark each spelling means.
 *
 * A block's body stores emphasis structurally, as a `marks` array on a run — this
 * is not that. `correctAnswer` is a single pipe-joined string (one answer per
 * input, by position), so there is nowhere on it to hang a runs array. Markup in
 * the string is the shape that fits, and the four spellings below are the whole
 * vocabulary.
 *
 * Longest-first: `**` must be tried before `*`, and `__` before `_`.
 */
const ANSWER_MARKUP = [
  { pattern: /\*\*([\s\S]+?)\*\*/, mark: 'bold' },
  { pattern: /__([\s\S]+?)__/, mark: 'underline' },
  { pattern: /~~([\s\S]+?)~~/, mark: 'strike' },
  { pattern: /\*([^*\n]+?)\*/, mark: 'italic' },
  { pattern: /_([^_\n]+?)_/, mark: 'italic' },
];

/**
 * Split an answer key into styled runs.
 *
 * Returns `{ text, runs }` — the plain reading, and the same content as
 * `[{ text, marks }]` for anything that draws it. Both are needed and for
 * different reasons:
 *
 *   - `text` is what the MATCHER compares and what the blank contributes to the
 *     page's word stream. A member never types the markers, and a highlight
 *     quoting the sentence must not contain them.
 *   - `runs` is what the settled field DRAWS, so an answer authored bold reads
 *     back bold once it is right.
 *
 * That split is the whole point: formatting is ignored while deciding, and
 * applied afterwards.
 *
 * Unmatched markers are left as literal text rather than guessed at — the same
 * rule parseEmphasis follows, so an authoring slip stays visible.
 */
export function parseAnswerMarkup(value) {
  const source = typeof value === 'string' ? value : '';
  if (!source) return { text: '', runs: [] };

  const runs = [];
  let rest = source;

  while (rest) {
    // The earliest marker of any kind wins, so nesting order in the source is
    // respected rather than the order of the table above.
    let best = null;
    for (const { pattern, mark } of ANSWER_MARKUP) {
      const found = rest.match(pattern);
      if (found && (best === null || found.index < best.found.index)) {
        best = { found, mark };
      }
    }

    if (!best) {
      runs.push({ text: rest, marks: [] });
      break;
    }

    const { found, mark } = best;
    if (found.index > 0) runs.push({ text: rest.slice(0, found.index), marks: [] });
    // Recurse into the marked span so `**_both_**` carries both marks.
    const inner = parseAnswerMarkup(found[1]);
    inner.runs.forEach(run =>
      runs.push({ text: run.text, marks: [...new Set([mark, ...run.marks])] }),
    );
    rest = rest.slice(found.index + found[0].length);
  }

  return { text: runs.map(run => run.text).join(''), runs };
}

const MARK_CLASSES = {
  bold: 'mark-bold',
  italic: 'mark-italic',
  underline: 'mark-underline',
  strike: 'mark-strike',
  strikethrough: 'mark-strike',
};

// Backend rich-text marks (e.g. ['underline']) to styling classes. Unknown marks
// are dropped rather than passed through, so a new backend mark cannot break
// rendering by emitting a class nothing styles.
export function marksClass(marks) {
  if (!Array.isArray(marks)) return '';
  return marks
    .map(mark => MARK_CLASSES[mark])
    .filter(Boolean)
    .join(' ');
}

/**
 * One word object: its text, where it sits on the page, and what highlight (if
 * any) currently covers it.
 *
 * Split out of processText because a fill-in-the-blank is a word too — it just
 * comes from an answer rather than from a run of prose.
 */
export function buildWord(text, globalIndex, highlightMap, localIndex = 0) {
  const highlight = highlightMap.get(globalIndex);
  return {
    text,
    localIndex,
    globalIndex,
    isHighlighted: !!highlight,
    highlightId: highlight?.id || null,
    highlightColor: highlight?.color || null,
    // Only the LAST word of a highlight carries the note glyph, so a highlight
    // spanning a sentence gets one marker rather than one per word. Without this
    // the reader has no way to tell, from the page, that a highlight has a note
    // on it — the note lives in the drawer, which only opens on a tap.
    //
    // Empty notes do not count. Opening the drawer auto-creates a note row, and
    // one that is never typed into is pruned on dismiss — counting it would flash
    // a glyph on and back off for a note the reader never wrote. Same rule the
    // delete confirmation uses for "and N notes".
    isNoteAnchor:
      globalIndex === highlight?.end &&
      (highlight?.notes || []).some(note => String(note?.text ?? '').trim().length > 0),
  };
}

// The content team marks emphasis inside a plain content string the way they
// write it, `**like this**` — it is how the Next Steps lessons bold the clause a
// verse turns on ("**I live by faith in the Son of God**", Galatians 2:20). The
// markers survive the design export, the sync and the seed untouched, so they
// arrive here verbatim and the reader used to print them as literal asterisks
// glued to the neighbouring words (`**I`, `God**,`).
const EMPHASIS_PATTERN = /\*\*([\s\S]+?)\*\*/g;

/**
 * Strip `**bold**` markers, and report where the emphasis landed.
 *
 * Returns the cleaned text plus the character ranges within it that were marked.
 * Ranges are computed against the CLEANED text, so they can be compared directly
 * against token offsets once the markers are gone.
 *
 * Marked-up text is rare, so the common path exits before allocating anything.
 */
export function parseEmphasis(text) {
  const source = typeof text === 'string' ? text : '';
  if (!source.includes('**')) return { text: source, boldRanges: [] };

  const boldRanges = [];
  let cleaned = '';
  let consumed = 0;
  let match;

  EMPHASIS_PATTERN.lastIndex = 0;
  while ((match = EMPHASIS_PATTERN.exec(source)) !== null) {
    cleaned += source.slice(consumed, match.index);
    const start = cleaned.length;
    cleaned += match[1];
    boldRanges.push({ start, end: cleaned.length });
    consumed = match.index + match[0].length;
  }

  // An unpaired `**` is left exactly as written rather than guessed at — silently
  // eating it would hide an authoring mistake that the asterisks make obvious.
  return { text: cleaned + source.slice(consumed), boldRanges };
}

// Emphasis applies to a word if the word overlaps a marked range at all. Ranges
// are authored around whole clauses, so this only ever decides the boundary
// words, and a partially covered word reads as bold rather than as neither.
function marksForSpan(start, end, boldRanges) {
  return boldRanges.some(range => start < range.end && end > range.start) ? ['bold'] : [];
}

// Words and the whitespace runs between them, in order. The one definition of a
// word for the page's tokeniser and for a settled blank answer's painted pieces.
export function splitWordsAndSpaces(text) {
  return text.match(/\S+|\s+/g) || [];
}

/**
 * Split text into words and the whitespace between them.
 *
 * Whitespace is kept as its own token, and each space is linked to the word on
 * either side of it. That is what lets a highlight paint the gap between two of
 * its own words while leaving the gap at its edges alone.
 *
 * Emphasis markers are removed BEFORE tokenising, so `globalIndex` counts the
 * words a reader can actually see. The markers in the live content are glued to
 * their words (`**I`, `God**,`), so removing them leaves the word COUNT
 * unchanged and every stored highlight index still points where it did.
 */
export function processText(text, startIdx, highlightMap) {
  const { text: source, boldRanges } = parseEmphasis(text);
  const parts = splitWordsAndSpaces(source);
  const words = [];
  const tokens = [];
  let offset = 0;

  parts.forEach(part => {
    const spanStart = offset;
    offset += part.length;

    if (/^\s+$/.test(part)) {
      tokens.push({
        type: 'space',
        text: part,
        previousWord: words[words.length - 1] || null,
        nextWord: null,
      });
      return;
    }

    const marks = marksForSpan(spanStart, offset, boldRanges);
    const built = buildWord(part, startIdx + words.length, highlightMap, words.length);
    const word = marks.length ? { ...built, marks } : built;

    // Spaces run before this word that have no following word yet are all
    // waiting for it — a run of them can only ever be closed by the next word.
    for (let i = tokens.length - 1; i >= 0; i--) {
      const token = tokens[i];
      if (token.type !== 'space' || token.nextWord) break;
      token.nextWord = word;
    }

    words.push(word);
    tokens.push({ type: 'word', word });
  });

  return { words, tokens };
}

export function processWords(text, startIdx, highlightMap) {
  return processText(text, startIdx, highlightMap).words;
}

/**
 * Join whitespace across inline segment boundaries.
 *
 * A paragraph is a list of inline segments, and a space can sit at the end of
 * one segment with its next word at the start of the following one. Each
 * segment is processed alone, so those links have to be made afterwards or a
 * highlight spanning the boundary leaves an unpainted gap.
 *
 * Mutates the tokens in place and returns the same array.
 */
export function linkInlineWhitespace(items) {
  let previousWord = null;
  let pendingSpaces = [];

  const reachWord = word => {
    pendingSpaces.forEach(spaceToken => {
      if (!spaceToken.nextWord) {
        spaceToken.nextWord = word;
      }
    });
    pendingSpaces = [];
    previousWord = word;
  };

  items.forEach(item => {
    // A blank is one word of the sentence. It carries no tokens, so without this
    // the spaces either side of it never learn their neighbour, and a highlight
    // running into or out of a blank leaves the gap beside it unpainted.
    if (item.type === 'blank' && item.word) {
      reachWord(item.word);
      return;
    }
    // A nested list breaks the run of prose it sits in: it owns whole lines, and
    // the space after it is not the space after the word before it. Without this
    // reset a highlight ending before the list and one starting after it would be
    // joined by a painted gap that spans the entire list.
    if (item.type === 'list') {
      previousWord = null;
      pendingSpaces = [];
      return;
    }
    if (!Array.isArray(item.tokens)) return;

    item.tokens.forEach(token => {
      if (token.type === 'space') {
        if (!token.previousWord && previousWord) {
          token.previousWord = previousWord;
        }
        pendingSpaces.push(token);
        return;
      }

      if (token.type === 'word') reachWord(token.word);
    });
  });

  return items;
}

/**
 * A verse's rich runs with their markdown stripped, `**bold**` excepted.
 *
 * A verse's plain mirror has always been built with stripInlineMarkup, so its text
 * reaches the reader markdown-free. Tokenising the rich body verbatim instead
 * would put `*faith*` and `~~things~~` on screen, complete with markers, the day a
 * verse gained one. Emphasis on a rich run comes from its `marks`; the markers are
 * the plain string's mechanism, and only `**` still has a reader downstream.
 *
 * Recurses into lists, whose lines are runs like any other.
 */
function stripRunMarkup(inline) {
  return (inline || []).map(seg => {
    if (Array.isArray(seg?.items)) {
      return {
        ...seg,
        items: seg.items.map(item => ({ ...item, inline: stripRunMarkup(item.inline) })),
      };
    }
    if (typeof seg?.text !== 'string') return seg;
    return { ...seg, text: stripInlineMarkupKeepingBold(seg.text) };
  });
}

/**
 * Process a page's blocks into their word lists.
 *
 * `globalIndex` runs continuously across the whole page in document order, so
 * a highlight can span blocks. Each block that consumed words is tagged with
 * the range it used (`wordRange`), which is how an end-of-block note row finds
 * the highlights terminating inside it.
 */
export function buildProcessedContent(content, highlightMap, blankOptions = {}) {
  let wordIndex = 0;

  const processWithWords = (text, block, preserveWhitespace = false) => {
    const processedText = processText(text, wordIndex, highlightMap);
    const words = processedText.words;
    wordIndex += words.length;
    return preserveWhitespace ? { ...block, ...processedText } : { ...block, words };
  };

  // One word for the blank, whatever is in it. `blankIndex` is which input on the
  // block it is, so the component can wire the word back to the answer it shows.
  const processBlank = (item, answer, blankIndex) => {
    const word = buildWord(blankWordText(answer), wordIndex, highlightMap);
    wordIndex += 1;
    return { ...item, type: 'blank', blankIndex, word };
  };

  /**
   * A list, wherever it appears — as a block of its own or as an inline item
   * inside a paragraph, a leader's note, an accordion or a blank's prompt.
   *
   * Items are numbered in document order, so `globalIndex` keeps running down the
   * list and a highlight can span from one bullet into the next. Whitespace is
   * linked WITHIN an item and deliberately not across items: the gap between two
   * bullets is not the gap between two words — it is a line break and a marker
   * column, and painting a highlight through it would run the background across
   * the markers.
   */
  // `processSegment` is passed in rather than closed over because a list item's
  // contents are processed by whatever rules the OWNING block uses — a blank block
  // numbers its inputs from a running counter, everything else reads a blank's
  // answer by id. Without this, a blank sitting inside a bullet fell through
  // unprocessed and rendered as nothing at all: no input, no word, silently.
  const processList = (list, processSegment) => {
    const items = (list.items || []).map(item => ({
      ...item,
      inline: linkInlineWhitespace((item.inline || []).map(processSegment)),
    }));
    return { ...list, items };
  };

  /**
   * One rich-text run: text, a nested list, or one of the paragraph's interactive
   * items. Shared by every block whose body is a run rather than a plain string,
   * so bold/italic/underline and bullets behave the same in all of them.
   */
  const processInlineItem = item => {
    if (item.type === 'list') return processList(item, processInlineItem);
    if (item.type === 'blank') {
      // An inline blank keys its answer off its own id rather than a position in
      // the block, so it reads its own entry straight out of the map.
      return processBlank(item, (blankOptions.answers || {})[item.id] || '', 0);
    }
    if (typeof item.text === 'string') return processWithWords(item.text, item, true);
    return item;
  };

  const processBlock = block => {
    if (block.type === 'paragraph') {
      const inline = block.inline.map(item => {
        if (item.type === 'text') {
          return processWithWords(item.text, item, true);
        }
        if (item.type === 'list') {
          return processList(item, processInlineItem);
        }
        if (item.type === 'toggle') {
          // Both states are indexed up front: the words a toggle can show are
          // fixed, and flipping it must not renumber the rest of the page.
          const wordsStateA = processWords(item.stateA || '', wordIndex, highlightMap);
          const wordsStateB = processWords(
            item.stateB || '',
            wordIndex + wordsStateA.length,
            highlightMap,
          );
          wordIndex += wordsStateA.length + wordsStateB.length;
          return { ...item, wordsStateA, wordsStateB };
        }
        if (item.type === 'popup-trigger') {
          return processWithWords(item.text || '', item);
        }
        if (item.type === 'blank') {
          // An inline blank keys its answer off its own id rather than a position
          // in the block, so it reads its own entry straight out of the map.
          return processBlank(item, (blankOptions.answers || {})[item.id] || '', 0);
        }
        // A run carrying text but no `type`. Tokenised, and stamped so the
        // renderer draws it: every other rich-text host already treats a bare
        // `{ text }` run as prose (see processInlineItem), and a paragraph was the
        // one place where such a run rendered as nothing at all — no words, no
        // error, the copy simply gone. Checked LAST so a typed item that also
        // carries `text`, like a popup trigger, still takes its own branch.
        if (typeof item.text === 'string') {
          return processWithWords(item.text, { ...item, type: 'text' }, true);
        }
        return item;
      });

      return { ...block, inline: linkInlineWhitespace(inline) };
    }

    if (block.type === 'heading' && block.content) {
      // A heading's words are page words like any other, so a rich body is
      // tokenised as runs and `words` is left off — collectWords reads both, and a
      // block carrying each would have its heading counted twice.
      //
      // Marks only, never a list: a heading is one line, and a <ul> is not valid
      // inside an <h2>. The same rule fieldRuns applies to the untouched fields has
      // to be applied HERE too, because a heading is tokenised and so never passes
      // through fieldRuns — without this check a list segment took the rich path,
      // carried no `tokens`, and the heading rendered completely empty.
      if (Array.isArray(block.inline) && block.inline.every(seg => seg?.type !== 'list')) {
        const inline = stripRunMarkup(block.inline).map(processInlineItem);
        return { ...block, inline: linkInlineWhitespace(inline) };
      }

      // `inline` is DROPPED rather than just ignored. The renderer chooses its
      // branch by which of the two is present, so a rich body left on the block
      // would win while carrying no tokens, and the heading would render empty.
      const plain = { ...block };
      delete plain.inline;
      return processWithWords(block.content, plain);
    }

    // A `blank` block is a prompt with inputs punched into it. The prompt's words
    // are tokenised like any other prose and each input becomes one word, so a
    // reader can mark a whole exercise sentence, gaps and all.
    if (block.type === 'blank') {
      const answers = blankAnswersFor(block, blankOptions);
      let blankIndex = 0;

      // Authored as rich text: runs carrying marks, `{ type: 'blank' }` items where
      // the inputs go, and nested lists. A blank's `blankIndex` comes from its
      // POSITION among the blank items, which is the same ordering the legacy
      // prompt split produces and the same one the stored answer string is joined
      // in — so a prompt converted from a string to runs keeps every answer on the
      // input it was typed into.
      if (Array.isArray(block.inline)) {
        // One counter for the whole block, walked in document order — a blank
        // inside a bullet takes the next index just as one in the prose does,
        // because the stored answer string is joined in exactly that order.
        const processBlankSegment = item => {
          if (item.type === 'blank') {
            const processed = processBlank(item, answers[blankIndex], blankIndex);
            blankIndex += 1;
            return processed;
          }
          if (item.type === 'list') return processList(item, processBlankSegment);
          if (typeof item.text === 'string') return processWithWords(item.text, item, true);
          return item;
        };

        const inline = block.inline.map(processBlankSegment);
        return { ...block, inline: linkInlineWhitespace(inline) };
      }

      const parts = String(block.prompt || '').split(BLANK_MARKER);
      const inline = [];
      parts.forEach((part, i) => {
        inline.push(processWithWords(part, { type: 'text' }, true));
        // One fewer blank than there are parts — the last part has nothing after it.
        if (i < parts.length - 1) inline.push(processBlank({}, answers[i], i));
      });
      return { ...block, inline: linkInlineWhitespace(inline) };
    }

    if (RICH_TEXT_BLOCK_TYPES.has(block.type)) {
      const inline = (block.inline || []).map(processInlineItem);
      const processed = { ...block, inline: linkInlineWhitespace(inline) };
      if (Array.isArray(block.blocks) && block.blocks.length) {
        processed.blocks = block.blocks.map(child => {
          const start = wordIndex;
          const nested = processBlock(child);
          if (wordIndex === start) return nested;
          return { ...nested, wordRange: { start, end: wordIndex - 1 } };
        });
      }
      return processed;
    }

    // A list is a run of items, each one the same `inline` shape a paragraph has.
    // Items are tokenised in document order so `globalIndex` keeps running down
    // the list and a highlight can span from one bullet into the next.
    //
    // Whitespace is linked WITHIN an item and deliberately not across items. The
    // gap between two bullets is not the gap between two words — it is a line
    // break and a marker column, and painting a highlight through it would run
    // the background across the markers.
    //
    // Routed through processList/processInlineItem rather than mapping the
    // segments here, which is what it used to do: that path tokenised a segment
    // only when it carried a string `text`, so a list NESTED under an item passed
    // through untouched and its words were never indexed. The identical list
    // nested inside a paragraph came out fine, because that path already
    // recursed — one of the two behaviours had to be wrong, and it was this one.
    if (block.type === 'list') {
      return processList(block, processInlineItem);
    }

    if (VERSE_BLOCK_TYPES.has(block.type)) {
      // The reference is numbered BEFORE the verse itself, because that is the
      // order it is read in — a selection running from the reference into the verse
      // has to be one unbroken range of indices.
      //
      // Numbered from the FORMATTED reference, which is what VerseCard displays:
      // the raw field can spell the version differently ("John 3:16 (nkjv)"), and a
      // word the reader cannot see is a word they cannot highlight.
      const referenceWords = processWords(
        formatVerseReference(block.reference, block.version),
        wordIndex,
        highlightMap,
      );
      wordIndex += referenceWords.length;

      // A rich body is tokenised as runs, the same way a quote's is: it can carry
      // real marks and bulleted lines, and both have to reach the renderer as
      // structure rather than as one flat string. `content` stays alongside as the
      // plain mirror the copy action reads.
      //
      // `inline` and `words` are deliberately EXCLUSIVE. collectWords reads both,
      // so a block carrying both would have every word of its body counted twice —
      // and that list is what a highlight's saved quote and the drag knobs' word
      // lookup are built from.
      if (Array.isArray(block.inline)) {
        const inline = stripRunMarkup(block.inline).map(processInlineItem);
        return { ...block, referenceWords, inline: linkInlineWhitespace(inline) };
      }

      const words = processWords(block.content || '', wordIndex, highlightMap);
      wordIndex += words.length;
      return { ...block, referenceWords, words };
    }

    return block;
  };

  return (content || []).map(block => {
    const start = wordIndex;
    const processed = processBlock(block);
    // Wordless blocks (images, dividers, banners, …) never own a note row.
    if (wordIndex === start) return processed;
    return { ...processed, wordRange: { start, end: wordIndex - 1 } };
  });
}

/**
 * Where each block's words start, and how many it owns.
 *
 * The store converts between the page-global indices the reader works in and the
 * block-scoped pair a highlight is persisted as, and it needs exactly this to do
 * it. It used to count words itself, with a simpler rule — a paragraph was its
 * `text` segments joined — which disagreed with the tokeniser above for any
 * paragraph carrying a toggle or a popup trigger, and counted `scripture` blocks
 * that render no words at all. A highlight saved past a block like that came back
 * from the API anchored to the wrong words, silently, on reload. There is one
 * counter now, and it is the one that lays out the page.
 *
 * Memoised on the blocks array: fetchLessonHighlights converts every highlight on
 * a lesson one at a time, and each conversion would otherwise re-tokenise the
 * whole page. The cache is keyed by identity and holds nothing alive, so a
 * re-normalised lesson simply misses.
 */
const blockOffsetCache = new WeakMap();

export function buildBlockOffsets(blocks) {
  if (!Array.isArray(blocks)) return {};
  const cached = blockOffsetCache.get(blocks);
  if (cached) return cached;

  const processed = buildProcessedContent(blocks, new Map());
  let offset = 0;
  const offsets = {};
  processed.forEach(block => {
    const range = block.wordRange;
    const wordCount = range ? range.end - range.start + 1 : 0;
    offsets[block.id] = { offset, wordCount };
    offset += wordCount;
  });

  blockOffsetCache.set(blocks, offsets);
  return offsets;
}

/**
 * Every word on the page, in document order.
 *
 * Built from the processed blocks rather than by walking the DOM: the word
 * objects already carry `globalIndex` and `text`, so this costs a walk of the
 * page's block list instead of materialising every span on it.
 */
export function collectWords(processedContent) {
  const words = [];
  const collect = list => {
    (list || []).forEach(word => words.push(word));
  };

  // Walked in array order, which is the order globalIndex was assigned — including
  // for a nested list, whose words are numbered at the point the list appears in
  // the run, and for blocks nested inside a Leader's Note or Accordion.
  //
  // A list keeps its words one level deeper, in items[].inline[]. Missing that
  // fails silently rather than loudly: the words still lay out and still
  // highlight, but every consumer of this list (the quote a highlight is saved
  // with, the copied text, the drag knobs' word lookup) skips straight past the
  // list, so a highlight on a bullet is stored with somebody else's words.
  const collectInline = inline => {
    (inline || []).forEach(item => {
      // A blank carries one word rather than a list of them.
      if (item.word) words.push(item.word);
      collect(item.words);
      collect(item.wordsStateA);
      collect(item.wordsStateB);
      (item.items || []).forEach(listItem => collectInline(listItem.inline));
    });
  };

  const collectBlock = block => {
    collect(block.referenceWords);
    collect(block.words);
    collectInline(block.inline);
    (block.items || []).forEach(item => collectInline(item.inline));
    (block.blocks || []).forEach(collectBlock);
  };

  (processedContent || []).forEach(collectBlock);

  return words;
}

// Already in document order, which is the order globalIndex was assigned.
export function textForRange(processedContent, start, end) {
  return collectWords(processedContent)
    .filter(word => word.globalIndex >= start && word.globalIndex <= end)
    .map(word => word.text)
    .join(' ');
}

/** True when chrome still has a body of its own, not only nested children. */
export function hasInlineBody(inline) {
  return (inline || []).some(seg => {
    if (!seg) return false;
    if (seg.type === 'list' || Array.isArray(seg.items)) {
      return (seg.items || []).some(item => hasInlineBody(item.inline));
    }
    if (seg.type === 'blank' || seg.word) return true;
    if (Array.isArray(seg.words) && seg.words.length) return true;
    if (Array.isArray(seg.tokens) && seg.tokens.some(token => token.type === 'word')) return true;
    return String(seg.text || '').trim().length > 0;
  });
}
